// Los equipos del cliente salen de la flota MDM del tenant filtrada por SUS créditos: no hay
// endpoint que los liste por cliente, y el expediente MDM cuelga del crédito, no del acreditado.
import { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { MdmOperationTypeEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationTypeEnum';
import {
    isCutoffPhase,
    NO_VALUE,
    readLockPhase,
    type MdmCutoffPhase,
    type MdmLockPhase,
} from '@/domains/credit/mdm/logic';
import type { MdmFleetDevice } from '@/domains/credit/mdm/types';
import { formatDateMx } from '@/lib/dateHelpers';
import type { CustomerCreditDetail } from '@/domains/sales/portal/customers/api/useCustomerCredit';
import { readFailureOf, type ListErrorCopy, type ReadFailure } from './creditCustomersErrors';

/** Motivos del bloqueo en corto, para el campo «Motivo» de la tarjeta. */
export const LOCK_REASON_LABEL: Record<LoanDeviceLockReasonEnum, string> = {
    [LoanDeviceLockReasonEnum.OVERDUE_T_PLUS_1]: 'Cuota vencida',
    [LoanDeviceLockReasonEnum.ADMIN_MANUAL]: 'Bloqueo manual',
    [LoanDeviceLockReasonEnum.FRAUD]: 'Fraude',
};

/** El motivo del bloqueo; hueco sin motivo o sin equipo LOCKED, porque un suelto puede conservarlo. */
export function lockReasonLabel(device: MdmFleetDevice): string {
    if (device.mdmStatus !== LoanDeviceMdmStatusEnum.LOCKED) return NO_VALUE;
    return device.lockReason === undefined ? NO_VALUE : LOCK_REASON_LABEL[device.lockReason];
}

/** La fecha del candado solo con la fase confirmada: sin evidencia, «bloqueado desde» miente. */
export function lockedSinceLabel(device: MdmFleetDevice, now: Date | string): string {
    if (readLockPhase(device, now) !== 'locked' || device.lockedAt === undefined) return NO_VALUE;
    return formatDateMx(device.lockedAt);
}

/**
 * Marca y modelo del equipo. El expediente MDM no los guarda todavía y llegan vacíos: el hueco se
 * dice, NUNCA se rellena con el SKU del producto financiado, que es otra cosa.
 */
export function deviceModelLabel(device: MdmFleetDevice): string {
    return [device.brand, device.model].filter(Boolean).join(' ').trim() || NO_VALUE;
}

export type CustomerDeviceEventKind = 'enrollment' | 'lock' | 'release';

/** Un hito del equipo, sintetizado del expediente: la bitácora repite y caduca por TTL. */
export interface CustomerDeviceEvent {
    kind: CustomerDeviceEventKind;
    /** ISO del hito; ausente cuando el expediente no guarda esa fecha. Nunca se inventa. */
    at?: string;
    title: string;
    subtitle: string;
}

/** El hito del bloqueo según su fase: solo el confirmado es «Bloqueo remoto». */
const LOCK_EVENT_TITLE: Record<MdmLockPhase, string> = {
    locked: 'Bloqueo remoto',
    unconfirmed: 'Orden de bloqueo remoto sin confirmar',
    refuted: 'Bloqueo remoto desmentido',
    scheduled: 'Bloqueo remoto programado',
    cutoffPassed: 'Corte vencido sin confirmar',
    cutoffRefuted: 'Corte desmentido',
};

/** El subtítulo del hito del corte vencido, según lo que dijo el proveedor. */
const CUTOFF_EVENT_SUBTITLE: Record<MdmCutoffPhase, string> = {
    cutoffPassed: 'El proveedor no confirma si el equipo quedó bloqueado',
    cutoffRefuted: 'El proveedor dice que el equipo sigue sin candado',
};

/**
 * Los hitos del equipo, del más reciente al más antiguo.
 *
 * Se arman al revés del ciclo de vida —desenlace primero, enrolamiento al final— para que el hito
 * sin fecha conserve su lugar en vez de caer al fondo por no tener con qué ordenarse.
 */
export function deviceEventsOf(
    device: MdmFleetDevice,
    now: Date | string = new Date(),
): CustomerDeviceEvent[] {
    const events: CustomerDeviceEvent[] = [];

    const lockPhase = readLockPhase(device, now);
    if (lockPhase !== null) {
        events.push({
            kind: 'lock',
            at: isCutoffPhase(lockPhase) ? device.currentLockScheduledFor : device.lockedAt,
            title: LOCK_EVENT_TITLE[lockPhase],
            subtitle: isCutoffPhase(lockPhase)
                ? CUTOFF_EVENT_SUBTITLE[lockPhase]
                : `Motivo: ${lockReasonLabel(device)}`,
        });
    }
    if (device.mdmStatus === LoanDeviceMdmStatusEnum.RELEASED) {
        events.push({
            kind: 'release',
            // El expediente no guarda `releasedAt`: la última operación solo fecha la liberación
            // cuando fue ella misma la que liberó.
            at:
                device.lastOperationType === MdmOperationTypeEnum.RELEASE
                    ? device.lastOperationAt
                    : undefined,
            title: 'Dispositivo liberado',
            subtitle: 'Crédito liquidado · Perfil MDM removido',
        });
    }
    if (device.enrolledAt !== undefined) {
        events.push({
            kind: 'enrollment',
            at: device.enrolledAt,
            title: 'Enrolado a MDM',
            subtitle: `Vínculo con crédito ${device.creditRef}`,
        });
    }

    // `sort` es estable: dos hitos con fecha se ordenan por ella y el que no la tiene se queda
    // donde lo dejó el ciclo de vida.
    return events.sort((a, b) => (a.at && b.at ? b.at.localeCompare(a.at) : 0));
}

/** Los créditos del acreditado, para leer el equipo de cada uno. Vacío cuando no se pudo leer. */
export function creditIdsOf(credit: CustomerCreditDetail): Set<string> {
    if (credit.state !== 'ready') return new Set();
    return new Set(credit.data.credits.map((entry) => entry.creditId));
}

/**
 * Los equipos de esos créditos, en orden alfabético por lo que la tarjeta pinta como título. Con el
 * modelo ausente el desempate manda, y el orden queda igual de estable.
 */
export function devicesOfCredits(
    devices: readonly MdmFleetDevice[],
    creditIds: ReadonlySet<string>,
): MdmFleetDevice[] {
    return devices
        .filter((device) => creditIds.has(device.creditId))
        .sort(
            (a, b) =>
                deviceModelLabel(a).localeCompare(deviceModelLabel(b), 'es-MX') ||
                a.creditRef.localeCompare(b.creditRef, 'es-MX') ||
                a.imei.localeCompare(b.imei),
        );
}

/** Cuál de las dos lecturas dejó a la pestaña sin equipos que pintar. */
export type DevicesReadSource = 'credits' | 'fleet';

/** En qué quedó la pestaña. */
export type CustomerDevicesRead =
    | { state: 'loading' }
    | { state: 'failed'; source: DevicesReadSource; failure: ReadFailure }
    | { state: 'empty' }
    | { state: 'devices'; devices: MdmFleetDevice[] };

/** Lo que la pestaña necesita de la lectura de equipos por crédito para resolver su desenlace. */
export interface DevicesFleetRead {
    devices: readonly MdmFleetDevice[];
    isLoading: boolean;
    error: Error | null;
}

export function customerDevicesReadOf(input: {
    credit: CustomerCreditDetail;
    fleet: DevicesFleetRead;
}): CustomerDevicesRead {
    const { credit, fleet } = input;

    if (credit.state === 'loading' || fleet.isLoading) return { state: 'loading' };
    if (credit.state === 'forbidden') {
        return { state: 'failed', source: 'credits', failure: 'forbidden' };
    }
    if (credit.state === 'error') return { state: 'failed', source: 'credits', failure: 'error' };
    if (fleet.error) {
        return { state: 'failed', source: 'fleet', failure: readFailureOf(fleet.error) ?? 'error' };
    }

    // Sin un solo crédito no puede haber equipo enrolado.
    const creditIds = creditIdsOf(credit);
    if (creditIds.size === 0) return { state: 'empty' };

    const devices = devicesOfCredits(fleet.devices, creditIds);
    if (devices.length === 0) return { state: 'empty' };
    return { state: 'devices', devices };
}

/** El texto del mock para el cliente que se leyó completo y no tiene un solo equipo. */
export const NO_DEVICES_LABEL = 'Este cliente no tiene dispositivos registrados.';

/** Qué decir cuando la pestaña se quedó sin equipos porque una de sus dos lecturas falló. */
export function devicesErrorCopy(source: DevicesReadSource, failure: ReadFailure): ListErrorCopy {
    if (source === 'credits') {
        if (failure === 'forbidden') {
            return {
                title: 'No tienes permiso para ver el crédito de este cliente',
                description:
                    'Los equipos se cruzan contra sus créditos y tu rol no incluye leerlos. Insistir no lo cambia; contacta a quien administra los roles.',
                canRetry: false,
            };
        }
        return {
            title: 'No se pudo leer el crédito de este cliente',
            description:
                'Sin sus créditos no se sabe qué equipos son suyos. Vuelve a intentar la lectura.',
            canRetry: true,
        };
    }
    if (failure === 'forbidden') {
        return {
            title: 'No tienes permiso para ver los equipos enrolados',
            description:
                'Los equipos se leen del fleet MDM, que es un permiso aparte del de la ficha. Insistir no lo cambia; contacta a quien administra los roles.',
            canRetry: false,
        };
    }
    return {
        title: 'No se pudo leer el fleet MDM',
        description:
            'La lectura de los equipos falló, así que de este cliente no se puede afirmar ni que tiene ni que no tiene. Vuelve a abrir la ficha en un minuto.',
        canRetry: false,
    };
}
